import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Leaf, Instagram, Phone, Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { BUSINESS } from "@/data/site";
import { WhatsAppIcon } from "@/components/Icons";
import { LUX_EASE } from "@/components/Reveal";

export default function Navbar() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  const links = [
    { to: "/", label: t.nav.home, id: "nav-link-home" },
    { to: "/about", label: t.nav.about, id: "nav-link-about" },
    { to: "/our-tea", label: t.nav.ourTea, id: "nav-link-our-tea" },
    { to: "/contact", label: t.nav.contact, id: "nav-link-contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-obsidian/85 backdrop-blur-md border-b hairline"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-2.5 text-gold"
          aria-label="KAZIM'S — home"
        >
          <Leaf className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-serif text-xl md:text-2xl font-semibold tracking-[0.22em] text-cream">
            KAZIM<span className="text-gold">'S</span>
          </span>
        </Link>

        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.id}
              className={({ isActive }) =>
                `relative text-[13px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 hover:text-gold ${
                  isActive ? "text-gold" : "text-cream/80"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-1/2 h-px -translate-x-1/2 bg-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LanguageSwitcher />
          <a
            href={BUSINESS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-link"
            aria-label="KAZIM'S on Instagram"
            className="text-cream/70 transition-colors hover:text-gold"
          >
            <Instagram className="h-[18px] w-[18px]" strokeWidth={1.5} />
          </a>
          <a
            href={BUSINESS.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-link"
            aria-label="Chat with KAZIM'S on WhatsApp"
            className="text-cream/70 transition-colors hover:text-gold"
          >
            <WhatsAppIcon className="h-[18px] w-[18px]" />
          </a>
          <Link
            to="/our-tea"
            data-testid="nav-explore-tea-button"
            className="bg-gold px-6 py-2.5 text-[13px] font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_28px_rgba(212,175,55,0.3)]"
          >
            {t.nav.explore}
          </Link>
        </div>

        <button
          className="flex h-11 w-11 items-center justify-center border hairline text-cream lg:hidden"
          onClick={() => setOpen(true)}
          data-testid="nav-hamburger-button"
          aria-label={t.nav.menuOpen}
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: LUX_EASE }}
            className="fixed inset-0 z-[60] flex h-screen flex-col bg-obsidian/[0.985] backdrop-blur-sm lg:hidden"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <span className="flex items-center gap-2.5">
                <Leaf className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <span className="font-serif text-xl font-semibold tracking-[0.22em] text-cream">
                  KAZIM<span className="text-gold">'S</span>
                </span>
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-close-button"
                aria-label={t.nav.menuClose}
                className="flex h-11 w-11 items-center justify-center border hairline text-cream"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.12 + i * 0.08, duration: 0.6, ease: LUX_EASE }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-${l.id}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block border-b hairline py-5 font-serif text-4xl transition-colors ${
                        isActive ? "text-gold" : "text-cream hover:text-gold"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6, ease: LUX_EASE }}
              className="flex items-center justify-between px-8 pb-10"
            >
              <LanguageSwitcher />
              <div className="flex items-center gap-5">
                <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" data-testid="mobile-instagram-link" aria-label="Instagram" className="text-cream/70 hover:text-gold">
                  <Instagram className="h-5 w-5" strokeWidth={1.5} />
                </a>
                <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-link" aria-label="WhatsApp" className="text-cream/70 hover:text-gold">
                  <WhatsAppIcon className="h-5 w-5" />
                </a>
                <a href={BUSINESS.tel} data-testid="mobile-call-link" aria-label="Call" className="text-cream/70 hover:text-gold">
                  <Phone className="h-5 w-5" strokeWidth={1.5} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
